import React from "react";
import { Link } from "react-router-dom";

const PRIVACY_LAST_UPDATED = "May 22, 2026";

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" subtitle={`Last updated · ${PRIVACY_LAST_UPDATED}`} testid="privacy-page">
      <p>
        Saathi is a free, multilingual AI companion for immigrants in Canada, built by <strong>Hikari Grove</strong>. We
        designed Saathi the way we'd want a tool for our own families: useful, calm, and respectful of your privacy. This
        page explains, in plain language, what we collect and what we don't.
      </p>

      <h2>What you can use Saathi without giving us</h2>
      <ul>
        <li>No name, email, or phone number is required to chat with Saathi.</li>
        <li>No account, no password, no sign-up.</li>
        <li>No tracking pixels, ad networks, or third-party analytics that profile you.</li>
      </ul>

      <h2>What we do collect</h2>
      <ul>
        <li>
          <strong>The messages you send Saathi</strong> and Saathi's replies — so Saathi can remember the conversation
          and so our team can improve the service.
        </li>
        <li>
          <strong>Voice recordings (if you use voice mode)</strong> are sent to OpenAI's Whisper service to be turned
          into text, then deleted from our servers. Only the resulting text is kept with the conversation.
        </li>
        <li>
          <strong>Documents you upload to the Document Reader</strong> (a tax letter, lease, benefit notice, etc.) are
          sent to Anthropic's Claude vision model to be explained. We store a log of when you uploaded a document and
          its size, plus the explanation we gave you, so we can debug issues and improve responses.
        </li>
        <li>
          <strong>The language you chose</strong> (Hindi, Punjabi, Urdu, Tamil, Bengali, Gujarati, Tagalog, Arabic, or
          English).
        </li>
        <li>
          <strong>Follow-up details — only if you ask for one</strong>: name, WhatsApp number, and the time of day that
          works for you. We only collect these when you fill in the follow-up form.
        </li>
      </ul>

      <h2>What we don't collect</h2>
      <ul>
        <li>Your IP address, location, or device fingerprint beyond what is in standard server logs.</li>
        <li>Cookies for advertising. We use no third-party advertising or marketing cookies.</li>
        <li>Anything you didn't type, speak, upload, or submit yourself.</li>
      </ul>

      <h2>How we use this information</h2>
      <ul>
        <li>To actually answer your questions in the conversation.</li>
        <li>So a human from the Hikari Grove team can call you on WhatsApp — only if you asked us to.</li>
        <li>To improve Saathi: which topics are confusing, which languages need more work, where Saathi makes
          mistakes. We may read your conversation to do this.</li>
        <li>To detect abuse and protect the service.</li>
      </ul>
      <p>We do <strong>not</strong> sell your data. We do not share your data with advertisers. We do not use your
        conversations to train external AI models.</p>

      <h2>Who we share data with</h2>
      <p>To run Saathi, we share the minimum necessary with these processors:</p>
      <ul>
        <li><strong>Anthropic</strong> (Claude Sonnet 4.5) — to generate Saathi's replies and read your uploaded documents.</li>
        <li><strong>OpenAI</strong> (Whisper for voice-to-text, TTS-1 for text-to-voice) — only when you use voice mode.</li>
        <li><strong>Emergent</strong> — our hosting and API gateway provider.</li>
        <li><strong>MongoDB Atlas</strong> — encrypted database storage.</li>
      </ul>
      <p>All processors are bound by their own enterprise privacy agreements. None of them are permitted to use your
        data to train general-purpose models.</p>

      <h2>How long we keep your data</h2>
      <ul>
        <li>Conversations: up to <strong>180 days</strong>, then automatically deleted unless flagged for safety review.</li>
        <li>Follow-up requests: kept until you've been contacted, then archived for up to <strong>12 months</strong>.</li>
        <li>Document upload logs: <strong>90 days</strong>.</li>
      </ul>

      <h2>Your rights</h2>
      <p>Wherever you live, you can ask us to:</p>
      <ul>
        <li>See what we have about you</li>
        <li>Correct anything that's wrong</li>
        <li>Delete it all</li>
        <li>Take it with you in a portable format</li>
      </ul>
      <p>Email <a href="mailto:privacy@hikarigrove.com">privacy@hikarigrove.com</a> with your WhatsApp number or
        conversation ID and we'll respond within 30 days. If you live in the EU/UK or California, the GDPR/CCPA give
        you these rights by law — we honour them for everyone.</p>

      <h2>Children</h2>
      <p>Saathi is not designed for children under 13. We do not knowingly collect data from anyone under 13.</p>

      <h2>Security</h2>
      <p>Data is encrypted in transit (HTTPS) and at rest. Access to the admin dashboard is gated by a secret token
        held only by Hikari Grove staff.</p>

      <h2>Important — Saathi is not legal, medical, or financial advice</h2>
      <p>Saathi is a friendly AI. It can be wrong. For binding decisions about your health, money, immigration status,
        or rights, please speak to a licensed professional.</p>

      <h2>Changes to this policy</h2>
      <p>If we change anything material, we'll update the date above and post a notice in Saathi for at least 30 days
        before the change takes effect.</p>

      <h2>Contact</h2>
      <p>
        Hikari Grove<br />
        Privacy questions: <a href="mailto:privacy@hikarigrove.com">privacy@hikarigrove.com</a><br />
        Everything else: <a href="mailto:hello@hikarigrove.com">hello@hikarigrove.com</a>
      </p>
    </LegalShell>
  );
}

const TERMS_LAST_UPDATED = "May 22, 2026";

export function Terms() {
  return (
    <LegalShell title="Terms of Use" subtitle={`Last updated · ${TERMS_LAST_UPDATED}`} testid="terms-page">
      <p>
        Welcome. By using Saathi at <strong>saathi.hikarigrove.com</strong> (the "Service"), you agree to these terms.
        They're written in plain language because we want you to actually read them.
      </p>

      <h2>1. Who we are</h2>
      <p>Saathi is operated by <strong>Hikari Grove</strong>, a Canadian project building tools for newcomers and
        diaspora communities. You can reach us at <a href="mailto:hello@hikarigrove.com">hello@hikarigrove.com</a>.</p>

      <h2>2. Saathi is free</h2>
      <p>We don't charge users. There's no subscription, no premium tier, no ads. If that ever changes for individuals,
        we'll tell you clearly before it does.</p>

      <h2>3. What Saathi is — and isn't</h2>
      <ul>
        <li>Saathi is an AI assistant that gives general information about life in Canada.</li>
        <li>Saathi is <strong>not</strong> a lawyer, doctor, immigration consultant, accountant, banker, or social
          worker.</li>
        <li>Saathi can make mistakes, hallucinate, or be out of date.</li>
        <li>For anything legally or medically serious — eviction notices, deportation, urgent health, large financial
          decisions — please speak to a qualified professional. Where useful, Saathi will point you to one.</li>
      </ul>

      <h2>4. What you agree to</h2>
      <ul>
        <li>You're at least 13 years old (or have a parent/guardian's permission).</li>
        <li>You won't use Saathi to harm others, spread hate, plan illegal activity, or attempt to extract or attack
          the underlying AI model.</li>
        <li>You won't upload documents that aren't yours or that contain other people's sensitive personal information
          without their consent.</li>
        <li>You understand voice recordings and uploaded documents are processed by AI services as described in our
          Privacy Policy.</li>
      </ul>

      <h2>5. What we agree to</h2>
      <ul>
        <li>We'll keep Saathi free for individuals.</li>
        <li>We'll treat your data the way we describe in the Privacy Policy.</li>
        <li>We'll respond to data requests within 30 days.</li>
        <li>We'll improve Saathi based on the feedback you give us — and tell our partners about it.</li>
      </ul>

      <h2>6. The fine print (warranty disclaimer)</h2>
      <p>Saathi is provided "as is." We don't warrant it will always be available, accurate, or fit for your specific
        situation. To the maximum extent permitted by law, Hikari Grove disclaims all warranties, express or implied.
        We are not liable for any losses arising from your reliance on Saathi's output — please verify important
        information with a qualified human professional.</p>

      <h2>7. Limitation of liability</h2>
      <p>If something goes wrong, our total liability to you for all claims relating to Saathi is limited to CAD $100.
        Some jurisdictions don't allow this limit; in those places it applies to the extent permitted by law.</p>

      <h2>8. Suspending or stopping the service</h2>
      <p>We may suspend or end Saathi at any time. If we shut it down, we'll publish a deprecation notice at least 30
        days in advance and give you a way to export your conversations.</p>

      <h2>9. For organizations</h2>
      <p>If you're a settlement agency, municipality, NGO, foundation, or government — these terms cover individual
        use of the public Saathi service. Custom deployments, white-labels, dashboards, and SLAs are governed by a
        separate agreement. Get in touch at <a href="mailto:hello@hikarigrove.com">hello@hikarigrove.com</a>.</p>

      <h2>10. Governing law</h2>
      <p>These terms are governed by the laws of Ontario, Canada, without regard to conflict-of-law rules. Disputes
        will be resolved in the courts of Ontario, unless a consumer-protection law in your jurisdiction gives you
        rights elsewhere.</p>

      <h2>11. Changes</h2>
      <p>If we materially change these terms, we'll update the date above and post a notice in Saathi for at least 30
        days before they take effect.</p>

      <h2>12. Contact</h2>
      <p><a href="mailto:hello@hikarigrove.com">hello@hikarigrove.com</a></p>
    </LegalShell>
  );
}

function LegalShell({ title, subtitle, children, testid }) {
  return (
    <div className="saathi-page min-h-screen" data-testid={testid}>
      <div className="saathi-texture" />
      <div className="relative z-10 max-w-2xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#8B7355] hover:text-[#E07B39]" data-testid="legal-back-home">
            ← Saathi
          </Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#8B7355]">Hikari Grove</div>
        </div>

        <header className="mb-10">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E07B39] mb-2">Legal</div>
          <h1 className="font-heading text-[44px] sm:text-[54px] leading-[1.02] text-[#1A1A2E] tracking-tight">{title}</h1>
          <p className="font-mono text-[11px] tracking-wider text-[#8B7355] mt-3">{subtitle}</p>
        </header>

        <article className="legal-prose">{children}</article>

        <footer className="mt-16 pt-8 border-t border-[#EDE0D4] flex items-center justify-between text-[12px] text-[#8B7355] font-mono">
          <Link to="/privacy" className="hover:text-[#E07B39]">Privacy</Link>
          <Link to="/terms" className="hover:text-[#E07B39]">Terms</Link>
          <a href="mailto:hello@hikarigrove.com" className="hover:text-[#E07B39]">Contact</a>
        </footer>
      </div>
    </div>
  );
}
